import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import AiAccelerator from '../../Components/AiAccelerate/aiAccelerator';
import GeneralViewer from '../../Components/GeneralViewer/GeneralViewer';
import { useGetStudyReportGeneralInfo } from '../../../helpers/api/reports/reports';
import Findings from '../../Components/Findings/Findings';
import ReportSection from '../../../../../../ui/src/components/MeasurementTable/Components/Report/ReportSection';
import { FiArrowUpRight, FiFile, FiFilm, FiLoader, FiMessageSquare, FiUser, FiX } from 'react-icons/fi';
import {
  CollaborationProvider,
  useCollaborationOptional,
} from '../../Components/Collaboration/CollaborationContext';
import { buildTargetFromNotificationData } from '../../Components/Collaboration/collaboration-types';
import { useDiscussionSummary } from '../../../helpers/api/discussions/discussions';
import { DicomViewer, VideoViewer } from '../../ComponnentsUI/Viewers/Viewers'; 
import { RiEyeLine } from 'react-icons/ri';
import { LuActivity } from 'react-icons/lu';
import { MdOutlineAssignment } from 'react-icons/md';
import { TbLayoutGrid } from 'react-icons/tb';
import { ReportForm } from '../../Components/report/report';
import { isReportSectionHash } from '../../ComponnentsUI/features/PrenatalExam/routing';
interface PatientInfo {
  name: string;
  sex: string;
  age: string;
  birthday: string;
}

interface PregnancyInfo {
  id?: number;
  estimatedDueDate: string;
  lastMenstrualPeriod: string;
  pregnancyType: string;
  pregnancyStatus: string;
  gestationalAgeWeeks?: number;
  gestationalAgeDays?: number;
  datingMethod?: string;
  preg_crl?: number;
  preg_bpd?: number;
  preg_hc?: number;
  preg_ac?: number;
  preg_fl?: number;
  grossesse_classee_a_risque?: boolean | null;
  risque_anemie?: boolean;
  risque_hta?: boolean;
  risque_diabete?: boolean;
  risque_cardiopathie?: boolean;
  risque_metrorragie?: boolean;
  risque_infection?: boolean;
  risque_preeclampsie?: boolean;
  risque_eclampsie?: boolean;
  risque_autre_details?: string | null;
}

interface StudyInfo {
  studyDate: string;
  studyId: string;
  studyType: string;
  category: string;
  series: Array<{
    serie_instance_uid: string;
    type: string;
  }>;
}

type AiAccelerateProps = {
  studyInstanceUID?: string | null;
  setIsAiAccelerate?: React.Dispatch<React.SetStateAction<boolean>>;
};

const CATEGORY_DISPLAY_MAP: Record<string, string> = {
  T1: 'Trimester 1',
  T2: 'Trimester 2',
  T3: 'Trimester 3',
};

function AiAccelerateContent({
  studyInstanceUID = null,
  setIsAiAccelerate = () => {},
}: AiAccelerateProps = {}) {
  const { t } = useTranslation('AiAccelerate');
  const { t: tCollab } = useTranslation('ClinicalCollaboration');
  const collaboration = useCollaborationOptional();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const collaborationDeepLinkHandled = useRef(false);
  const opensReportSection = isReportSectionHash(location.hash);
  const studyInstanceUIDs =
    searchParams.get('StudyInstanceUIDs') || studyInstanceUID;
  const initialSopInstanceUID = searchParams.get('sopInstanceUID');
  // frameId is the DB primary key of the clicked frame — unique, ordering-independent.
  // The viewer looks it up in allFramesMeta via frame.frame_id to build currentImageId.
  const initialFrameId = searchParams.get('frameId');
  const [activeTab, setActiveTab] = useState<
    'views' | 'findings' | 'report' | 'generalViewer'
  >(opensReportSection ? 'report' : 'generalViewer');

  // Close collaboration only when the user switches tabs (not when the panel opens).
  const previousTabRef = useRef(activeTab);
  const skipCollaborationCloseRef = useRef(false);
  useEffect(() => {
    if (previousTabRef.current === activeTab) return;
    previousTabRef.current = activeTab;
    if (skipCollaborationCloseRef.current) {
      skipCollaborationCloseRef.current = false;
      return;
    }
    collaboration?.closeCollaboration();
  }, [activeTab, collaboration?.closeCollaboration]);
  const [hasResolvedInitialTab, setHasResolvedInitialTab] = useState(
    opensReportSection
  );
  // When set, the General Viewer opens with this view pre-selected for assignment.
  const [pendingAssignViewCode, setPendingAssignViewCode] = useState<string | null>(null);
  // After "Confirm & Save" in the General Viewer, auto-navigate to this view's details.
  const [pendingSelectViewCode, setPendingSelectViewCode] = useState<string | null>(null);
  const [patientId, setPatientId] = useState<string>('');
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    name: '',
    sex: '',
    age: '',
    birthday: '',
  });
  const [pregnancyInfo, setPregnancyInfo] = useState<PregnancyInfo>({
    id: undefined,
    estimatedDueDate: '',
    lastMenstrualPeriod: '',
    pregnancyType: '',
    pregnancyStatus: '',
    gestationalAgeWeeks: undefined,
    gestationalAgeDays: undefined,
    datingMethod: undefined,
    preg_crl: undefined,
    preg_bpd: undefined,
    preg_hc: undefined,
    preg_ac: undefined,
    preg_fl: undefined,
  });
  const [studyInfo, setStudyInfo] = useState<StudyInfo>({
    studyDate: '',
    studyId: '',
    studyType: '',
    category: '',
    series: [],
  });
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState<{
    type: 'video' | 'dicom';
    file: File;
  } | null>(null);
  const [backendPreviews, setBackendPreviews] = useState<any[]>([]);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
  const [selectedFrameViewIds, setSelectedFrameViewIds] = useState<
    Record<string, string>
  >({});
  const queryClient = useQueryClient();
  const {
    data: studyReportGeneralInfo,
    isLoading: isLoadingStudyReportGeneralInfo,
    isError: isErrorStudyReportGeneralInfo,
  } = useGetStudyReportGeneralInfo(studyInstanceUIDs);
  const { data: collaborationSummary } = useDiscussionSummary(
    studyInstanceUIDs || undefined
  );
  const collaborationBadgeCount =
    (collaborationSummary?.unread_for_current_user ?? 0) +
    (collaborationSummary?.open_opinion_requests ?? 0);

  const noInstanceStudy = useMemo(() => {
    const studyData = studyReportGeneralInfo?.data?.study;
    const previews =
      studyReportGeneralInfo?.data?.previews ??
      studyReportGeneralInfo?.data?.preview;
    const seriesCount = Array.isArray(studyData?.series)
      ? studyData.series.length
      : 0;
    const previewCount = Array.isArray(previews) ? previews.length : 0;

    return seriesCount === 0 && previewCount === 0;
  }, [studyReportGeneralInfo]);

  useEffect(() => {
    if (opensReportSection) {
      setActiveTab('report');
      setHasResolvedInitialTab(true);
      return;
    }

    setActiveTab('generalViewer');
    setHasResolvedInitialTab(false);
  }, [opensReportSection, studyInstanceUIDs]);

  useEffect(() => {
    if (
      !studyInstanceUIDs ||
      isLoadingStudyReportGeneralInfo ||
      hasResolvedInitialTab
    ) {
      return;
    }

    setActiveTab(opensReportSection || noInstanceStudy ? 'report' : 'generalViewer');
    setHasResolvedInitialTab(true);
  }, [
    hasResolvedInitialTab,
    isLoadingStudyReportGeneralInfo,
    noInstanceStudy,
    opensReportSection,
    studyInstanceUIDs,
  ]);

  // When user switches to General Viewer tab, invalidate instance frames so they refetch (fixes 2nd+ time not showing changes from normal viewer)
  useEffect(() => {
    if (activeTab === 'generalViewer' && studyInstanceUIDs) {
      queryClient.invalidateQueries({
        queryKey: ['general-viewer-instance-frames', studyInstanceUIDs],
      });
    }
  }, [activeTab, studyInstanceUIDs, queryClient]);

  useEffect(() => {
    collaborationDeepLinkHandled.current = false;
  }, [studyInstanceUIDs]);

  useEffect(() => {
    if (!collaboration || !studyInstanceUIDs) return;
    if (searchParams.get('panel') !== 'collaboration') {
      collaborationDeepLinkHandled.current = false;
      return;
    }
    if (collaborationDeepLinkHandled.current) return;
    collaborationDeepLinkHandled.current = true;

    const tabParam = searchParams.get('tab');
    if (
      tabParam === 'generalViewer' ||
      tabParam === 'findings' ||
      tabParam === 'report' ||
      tabParam === 'views'
    ) {
      if (activeTab !== tabParam) {
        skipCollaborationCloseRef.current = true;
      }
      setActiveTab(tabParam);
      setHasResolvedInitialTab(true);
    }

    const discussionId = searchParams.get('discussionId');
    const targetType = searchParams.get('target_type');
    const targetRef = searchParams.get('target_ref');
    const targetLabel = searchParams.get('target_label');
    const target =
      targetType && targetRef
        ? buildTargetFromNotificationData(
            targetType,
            targetRef,
            studyInstanceUIDs,
            targetLabel
          )
        : undefined;

    collaboration.openCollaboration(target, discussionId);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('panel');
    nextParams.delete('discussionId');
    nextParams.delete('target_type');
    nextParams.delete('target_ref');
    nextParams.delete('target_label');
    nextParams.delete('tab');
    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true }
    );
  }, [
    activeTab,
    collaboration,
    studyInstanceUIDs,
    searchParams,
    navigate,
    location.pathname,
    location.hash,
  ]);

  // Helper function to calculate age from birth date
  const calculateAge = (birthDate: string) => {
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return `${age} years`;
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryDisplayName = (categoryCode: string): string => {
    return CATEGORY_DISPLAY_MAP[categoryCode] || categoryCode;
  };

  const handleFrameViewUpdated = (viewName: string, frameViewId: string) => {
    setSelectedFrameViewIds((prev) => ({
      ...prev,
      [viewName]: frameViewId,
    }));
  };

  const handlePregnancyUpdate = (updatedPregnancy: any) => {
    console.log('handlePregnancyUpdate called with:', updatedPregnancy);

    // Check if updatedPregnancy is valid
    if (!updatedPregnancy) {
      console.error('updatedPregnancy is undefined or null');
      return;
    }

    // Handle different response structures - sometimes the data might be nested
    const pregnancyData = updatedPregnancy.data || updatedPregnancy;
    console.log('Extracted pregnancy data:', pregnancyData);

    // Update the pregnancy info state with the new data
    setPregnancyInfo((prev) => {
      let estimatedDueDate = prev.estimatedDueDate;

      // If backend provided updated EDD, use it
      if (pregnancyData.estimated_due_date) {
        estimatedDueDate = formatDate(pregnancyData.estimated_due_date);
      }
      // If LMP was updated but no EDD in response, calculate it on frontend
      else if (pregnancyData.last_menstrual_period) {
        try {
          const lmpDate = new Date(pregnancyData.last_menstrual_period);
          if (!isNaN(lmpDate.getTime())) {
            const eddDate = new Date(
              lmpDate.getTime() + 280 * 24 * 60 * 60 * 1000
            ); // Add 280 days
            estimatedDueDate = formatDate(eddDate.toISOString());
          }
        } catch (error) {
          console.error('Error calculating EDD:', error);
        }
      }

      const updated = {
        ...prev,
        id: pregnancyData.id || prev.id,
        estimatedDueDate,
        lastMenstrualPeriod: pregnancyData.last_menstrual_period
          ? formatDate(pregnancyData.last_menstrual_period)
          : prev.lastMenstrualPeriod,
        pregnancyStatus: pregnancyData.pregnancy_status || prev.pregnancyStatus,
        gestationalAgeWeeks:
          pregnancyData.gestational_age_weeks !== undefined
            ? pregnancyData.gestational_age_weeks
            : prev.gestationalAgeWeeks,
        gestationalAgeDays:
          pregnancyData.gestational_age_days !== undefined
            ? pregnancyData.gestational_age_days
            : prev.gestationalAgeDays,
        datingMethod: pregnancyData.dating_method || prev.datingMethod,
        preg_crl:
          pregnancyData.preg_crl !== undefined
            ? pregnancyData.preg_crl
            : prev.preg_crl,
        preg_bpd:
          pregnancyData.preg_bpd !== undefined
            ? pregnancyData.preg_bpd
            : prev.preg_bpd,
        preg_hc:
          pregnancyData.preg_hc !== undefined
            ? pregnancyData.preg_hc
            : prev.preg_hc,
        preg_ac:
          pregnancyData.preg_ac !== undefined
            ? pregnancyData.preg_ac
            : prev.preg_ac,
        preg_fl:
          pregnancyData.preg_fl !== undefined
            ? pregnancyData.preg_fl
            : prev.preg_fl,
        grossesse_classee_a_risque:
          pregnancyData.grossesse_classee_a_risque !== undefined
            ? pregnancyData.grossesse_classee_a_risque
            : prev.grossesse_classee_a_risque,
        risque_anemie: pregnancyData.risque_anemie ?? prev.risque_anemie,
        risque_hta: pregnancyData.risque_hta ?? prev.risque_hta,
        risque_diabete: pregnancyData.risque_diabete ?? prev.risque_diabete,
        risque_cardiopathie:
          pregnancyData.risque_cardiopathie ?? prev.risque_cardiopathie,
        risque_metrorragie:
          pregnancyData.risque_metrorragie ?? prev.risque_metrorragie,
        risque_infection:
          pregnancyData.risque_infection ?? prev.risque_infection,
        risque_preeclampsie:
          pregnancyData.risque_preeclampsie ?? prev.risque_preeclampsie,
        risque_eclampsie:
          pregnancyData.risque_eclampsie ?? prev.risque_eclampsie,
        risque_autre_details:
          pregnancyData.risque_autre_details !== undefined
            ? pregnancyData.risque_autre_details
            : prev.risque_autre_details,
      };
      console.log('Updated pregnancy info state:', updated);
      return updated;
    });
  };

  useEffect(() => {
    if (studyReportGeneralInfo?.data && !isLoadingStudyReportGeneralInfo) {
      const { patient, study, views, pregnancy, preview } =
        studyReportGeneralInfo.data;
      if (studyReportGeneralInfo.data.previews) {
        setBackendPreviews(studyReportGeneralInfo.data.previews);
      }
      if (patient) {
        setPatientId(patient.id || '');
        const fullName = `${patient.patient_name || ''}${
          patient.patient_last_name ? ' ' + patient.patient_last_name : ''
        }`.trim();
        setPatientInfo({
          name: fullName || 'Unknown Patient',
          sex: 'Unknown', // Sex not provided in API response
          age: patient.birth_date
            ? calculateAge(patient.birth_date)
            : 'Unknown',
          birthday: patient.birth_date
            ? formatDate(patient.birth_date)
            : 'Unknown',
        });
      }

      if (pregnancy) {
        setPregnancyInfo({
          id: pregnancy.id,
          estimatedDueDate: pregnancy.estimated_due_date
            ? formatDate(pregnancy.estimated_due_date)
            : 'Unknown',
          lastMenstrualPeriod: pregnancy.last_menstrual_period
            ? formatDate(pregnancy.last_menstrual_period)
            : 'Unknown',
          pregnancyType: pregnancy.pregnancy_type || 'Unknown',
          pregnancyStatus: pregnancy.pregnancy_status || 'Unknown',
          gestationalAgeWeeks: pregnancy.gestational_age_weeks,
          gestationalAgeDays: pregnancy.gestational_age_days,
          datingMethod: pregnancy.dating_method,
          preg_crl: pregnancy.preg_crl,
          preg_bpd: pregnancy.preg_bpd,
          preg_hc: pregnancy.preg_hc,
          preg_ac: pregnancy.preg_ac,
          preg_fl: pregnancy.preg_fl,
          grossesse_classee_a_risque:
            pregnancy.grossesse_classee_a_risque ?? null,
          risque_anemie: pregnancy.risque_anemie === true,
          risque_hta: pregnancy.risque_hta === true,
          risque_diabete: pregnancy.risque_diabete === true,
          risque_cardiopathie: pregnancy.risque_cardiopathie === true,
          risque_metrorragie: pregnancy.risque_metrorragie === true,
          risque_infection: pregnancy.risque_infection === true,
          risque_preeclampsie: pregnancy.risque_preeclampsie === true,
          risque_eclampsie: pregnancy.risque_eclampsie === true,
          risque_autre_details: pregnancy.risque_autre_details || '',
        });
      }
      if (preview) {
        setBackendPreviews(preview);
      }

      if (study) {
        setStudyInfo({
          studyDate: study.study_date
            ? formatDate(study.study_date)
            : 'Unknown',
          studyId: study.study_instance_uid || 'Unknown',
          studyType: study.type || 'Unknown',
          category: study.category || 'Unknown',
          series: study.series || [],
        });
      }

      if (views && Array.isArray(views)) {
        const frameViewsByViewName = views.reduce(
          (acc, view) => {
            if (view.view_name && view.frameviewid) {
              acc[view.view_name] = view.frameviewid;
            }
            return acc;
          },
          {} as Record<string, string>
        );
        setSelectedFrameViewIds(frameViewsByViewName);
      }
    }
  }, [studyReportGeneralInfo, isLoadingStudyReportGeneralInfo]);

  if (!studyInstanceUIDs) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-center max-w-md">
          <div className="text-red-500 text-2xl mb-2">⚠</div>
          <p className="text-red-600 dark:text-red-400 font-medium">
            No Study ID provided
          </p>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Please provide a valid StudyInstanceUIDs parameter in the URL.
          </p>
        </div>
      </div>
    );
  }

  if (isErrorStudyReportGeneralInfo) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-center max-w-md">
          <div className="text-red-500 text-2xl mb-2">⚠</div>
          <p className="text-red-600 dark:text-red-400 font-medium">
            Error loading study information
          </p>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            There was an error fetching the study details. Please try again.
          </p>
        </div>
      </div>
    );
  }
  const handleOpenPreview = async (index: number = 0) => {
    const target = backendPreviews[index];
    if (!target?.url) return;

    setIsFetchingPreview(true);
    try {
      const response = await fetch(target.url);
      if (!response.ok) throw new Error('Failed to fetch');
      const blob = await response.blob();

      const extension = target.type === 'video' ? 'mp4' : 'dcm';
      const file = new File([blob], `file_${index}.${extension}`, {
        type: blob.type,
      });

      setSelectedPreviewIndex(index);
      setPreviewItem({ type: target.type, file });
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setIsFetchingPreview(false);
    }
  };
  const hasPreviews = backendPreviews && backendPreviews.length > 0;
  const previewCount = backendPreviews?.length || 0;
  const showInitialViewerLoader = !hasResolvedInitialTab;

  // Group previews by series for sidebar (preserve flat index for selection)
  const previewsBySeries = useMemo(() => {
    if (!backendPreviews?.length) return [];
    const bySeries = new Map<
      string,
      { flatIndex: number; item: (typeof backendPreviews)[0] }[]
    >();
    backendPreviews.forEach((item, flatIndex) => {
      const key = item.serie_instance_uid ?? `single-${flatIndex}`;
      if (!bySeries.has(key)) bySeries.set(key, []);
      bySeries.get(key)!.push({ flatIndex, item });
    });
    return Array.from(bySeries.entries()).map(
      ([seriesUid, instances], seriesIndex) => ({
        seriesIndex: seriesIndex + 1,
        seriesUid,
        instances,
      })
    );
  }, [backendPreviews]);

  const hasMultipleSeries = previewsBySeries.length > 1;

  const selectedSeriesLabel = useMemo(() => {
    if (!hasMultipleSeries || previewsBySeries.length === 0) return null;
    let acc = 0;
    for (const { seriesIndex, instances } of previewsBySeries) {
      const idx = instances.findIndex(
        ({ flatIndex }) => flatIndex === selectedPreviewIndex
      );
      if (idx >= 0)
        return {
          seriesIndex,
          instanceInSeries: idx + 1,
          totalInSeries: instances.length,
        };
      acc += instances.length;
    }
    return null;
  }, [hasMultipleSeries, previewsBySeries, selectedPreviewIndex]);

  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-gray-900 p-6">
      <header className="flex-shrink-0 bg-white dark:bg-gray-900 px-6 py-2.5 shadow-sm border-b border-slate-200 dark:border-gray-700 rounded-lg">
        <div className="w-full mx-auto flex items-center justify-between gap-4">
          {/* 1. Brand/Title Area */}
          <div className="flex items-center gap-4 min-w-max">
            <h1 className="font-bold text-gray-900 dark:text-white tracking-tight">
              {t('title')}
            </h1>
            <div className="h-6 w-px bg-gray-200 dark:bg-gray-800 hidden sm:block"></div>
          </div>

          {/* 2. Integrated Navigation (Segmented Control) */}
          <nav className="flex items-center p-1 bg-slate-100 dark:bg-gray-800/50 rounded-xl border border-slate-200 dark:border-gray-700">
            {[
              { id: 'generalViewer', icon: <TbLayoutGrid size={18}/>, label: t('tabs.generalViewer') },
              { id: 'views', icon: <RiEyeLine size={18} />, label: t('tabs.views') },
              {
                id: 'findings',
                icon: <LuActivity size={18} />,
                label: t('tabs.findings'),
              },
              {
                id: 'report',
                icon: <MdOutlineAssignment size={18} />,
                label: t('tabs.report'),
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id !== 'generalViewer') setPendingAssignViewCode(null);
                  setActiveTab(tab.id as any);
                }}
                className={`flex items-center gap-2 px-5 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon}
                <span className="hidden lg:inline">{tab.label}</span>
              </button>
            ))}
          </nav>

          {/* 3. Action Buttons Area */}
          <div className="flex items-center gap-2">
            {studyInstanceUIDs && (
              <button
                type="button"
                onClick={() => collaboration?.openCollaboration()}
                className="relative flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                title={tCollab('openPanel')}
              >
                <FiMessageSquare size={16} />
                <span className="hidden sm:inline">{tCollab('openPanel')}</span>
                {collaborationBadgeCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                    {collaborationBadgeCount > 9 ? '9+' : collaborationBadgeCount}
                  </span>
                )}
              </button>
            )}
            {patientId ? (
              <Link
                to={`/patients/${encodeURIComponent(patientId)}`}
                title={t('actions.patientProfile')}
                className="group flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-500/40 dark:hover:bg-blue-500/10 dark:hover:text-blue-300"
              >
                <FiUser className="h-4 w-4" aria-hidden="true" />
                <span className="hidden lg:inline">
                  {t('actions.patientProfile')}
                </span>
                <FiArrowUpRight
                  className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </Link>
            ) : null}
            {studyInstanceUID && (
              <button
                onClick={() => setIsAiAccelerate(false)}
                className="p-2 text-gray-500 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title={t('actions.backToGrid')}
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Tab Content - Flexible height with scrolling */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="h-full max-w-7xl mx-auto">
          {showInitialViewerLoader ? (
            <div className="h-full flex items-center justify-center">
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <FiLoader className="animate-spin text-blue-500" />
                <span>Loading exam viewer...</span>
              </div>
            </div>
          ) : activeTab === 'findings' ? (
            <div className="h-full">
              <Findings studyInstanceUid={studyInstanceUIDs || ''} />
            </div>
          ) : activeTab === 'views' ? (
            <AiAccelerator
              studyUID={studyInstanceUIDs}
              selectedFrameViewIds={selectedFrameViewIds}
              onFrameViewUpdated={handleFrameViewUpdated}
              availableSeries={studyInfo.series}
              onAssignInGeneralViewer={(view) => {
                setPendingAssignViewCode(view.view_code ?? null);
                setActiveTab('generalViewer');
              }}
              pendingSelectViewCode={pendingSelectViewCode}
              onPendingSelectHandled={() => setPendingSelectViewCode(null)}
            />
          ) : null}
          {/* {activeTab === 'report' && (
            <div className="h-full">
              <ReportSection
                studyInfo={studyInfo}
                patientInfo={patientInfo}
                pregnancyInfo={pregnancyInfo}
                isLoadingExamInfo={isLoadingStudyReportGeneralInfo}
                onPregnancyUpdate={handlePregnancyUpdate}
                patientId={patientId}
              />
            </div>
          )} */}
          {!showInitialViewerLoader && activeTab === 'report' && (
            <ReportForm
              studyId={studyInstanceUIDs || ''}
              patientInfo={patientInfo}
              pregnancyInfo={pregnancyInfo}
              studyInfo={studyInfo}
              isLoadingExamInfo={isLoadingStudyReportGeneralInfo}
              hasInstances={!noInstanceStudy}
            />
          )}
          {!showInitialViewerLoader && activeTab === 'generalViewer' && (
            <div className="h-full">
              <GeneralViewer
                key={studyInstanceUIDs}
                studyUID={studyInstanceUIDs}
                availableSeries={studyInfo.series}
                initialPendingViewCode={pendingAssignViewCode}
                onViewConfirmedAndSaved={() => {
                  // Remember which view was assigned so AiAccelerator can navigate
                  // directly to its View Details once reportViews refreshes.
                  if (pendingAssignViewCode) setPendingSelectViewCode(pendingAssignViewCode);
                  setPendingAssignViewCode(null);
                  setActiveTab('views');
                }}
                initialSopInstanceUID={initialSopInstanceUID}
                initialFrameId={initialFrameId}
              />
            </div>
          )}
        </div>
      </div>

      {/* PREVIEW MODAL */}
      {previewItem && (
        <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col">
          {/* Header */}
          <div className="h-14 flex items-center justify-between px-6 bg-white/5 border-b border-white/10 shrink-0">
            <div className="text-white font-medium flex items-center gap-2">
              {previewItem.type === 'dicom' ? (
                <FiFile className="text-blue-400" />
              ) : (
                <FiFilm className="text-purple-400" />
              )}
              <span>
                Instance {selectedPreviewIndex + 1}
                {previewCount > 1 ? ` of ${previewCount}` : ''}
                {selectedSeriesLabel && (
                  <span className="text-gray-400 font-normal ml-1">
                    · Series {selectedSeriesLabel.seriesIndex} (instance{' '}
                    {selectedSeriesLabel.instanceInSeries}/
                    {selectedSeriesLabel.totalInSeries})
                  </span>
                )}
              </span>
            </div>
            <button
              onClick={() => setPreviewItem(null)}
              className="text-white/70 hover:text-white bg-white/10 p-2 rounded-full"
            >
              <FiX size={20} />
            </button>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* SIDEBAR: Show only if multiple items exist */}
            {backendPreviews.length > 1 && (
              <div className="w-64 bg-black/40 border-r border-white/10 overflow-y-auto p-2 flex flex-col gap-2">
                <p className="text-[10px] text-gray-500 font-bold px-2 py-1">
                  SELECT INSTANCE
                </p>
                {previewsBySeries.map(({ seriesIndex, instances }) => (
                  <div key={seriesIndex} className="flex flex-col gap-1">
                    {hasMultipleSeries && (
                      <p className="text-[10px] text-gray-400 font-semibold px-2 py-1.5 mt-2 first:mt-0 border-b border-white/10">
                        Series {seriesIndex}
                      </p>
                    )}
                    {instances.map(({ flatIndex, item }, instanceIdx) => (
                      <button
                        key={flatIndex}
                        onClick={() => handleOpenPreview(flatIndex)}
                        className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all ${
                          selectedPreviewIndex === flatIndex
                            ? 'bg-blue-600/20 border border-blue-500/50'
                            : 'hover:bg-white/5 border border-transparent'
                        }`}
                      >
                        {item.type === 'video' ? (
                          <FiFilm className="text-purple-400 shrink-0" />
                        ) : (
                          <FiFile className="text-blue-400 shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="text-xs text-white font-medium truncate">
                            Instance {instanceIdx + 1}
                          </p>
                          <p className="text-[9px] text-gray-500 truncate">
                            {item.sop_instance_uid?.slice(-8) ?? ''}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* VIEWER AREA */}
            <div className="flex-1 p-8 flex items-center justify-center relative">
              {isFetchingPreview && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                  <FiLoader className="animate-spin text-white" size={30} />
                </div>
              )}
              <div className="max-w-6xl w-full h-full max-h-[85vh] bg-gray-900 rounded-lg overflow-hidden shadow-2xl border border-white/10">
                {previewItem.type === 'video' ? (
                  <VideoViewer file={previewItem.file} />
                ) : (
                  <DicomViewer file={previewItem.file} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function AiAccelerate(props: AiAccelerateProps = {}) {
  const [searchParams] = useSearchParams();
  const studyInstanceUIDs =
    searchParams.get('StudyInstanceUIDs') || props.studyInstanceUID || '';

  return (
    <CollaborationProvider studyInstanceUid={studyInstanceUIDs}>
      <AiAccelerateContent {...props} />
    </CollaborationProvider>
  );
}

export default AiAccelerate;
